/**
 * URL 관련 유틸리티 함수들
 * return_url 처리를 일관성 있게 하기 위한 헬퍼 함수들
 */

import { Locales, localeToCountryUrlPrefix } from '../../utils/language'

/**
 * 현재 페이지의 전체 URL을 가져옵니다 (pathname + search + hash)
 * @returns 현재 페이지의 전체 URL
 */
export const getCurrentUrl = (): string => {
  if (typeof window === 'undefined') {
    return '/'
  }
  return `${window.location.pathname}${window.location.search}${window.location.hash}`
}

/**
 * return_url을 포함한 로그인 페이지 URL을 생성합니다
 * @param returnUrl 리다이렉트할 URL (순수 path, prefix 없이 전달)
 * @param locale 현재 로케일 (return_url에 적절한 국가별 prefix 추가)
 * @param signinPath 로그인 페이지 경로 (기본값: '/signin')
 * @returns 로케일 기반 prefix가 적용된 return_url을 포함한 로그인 URL
 */
export const createSignInUrl = (returnUrl: string, locale: Locales, signinPath = '/signin'): string => {
  if (!returnUrl || returnUrl === '/') {
    return signinPath
  }

  // return_url에 locale 기반 country prefix 적용
  const prefixedReturnUrl = `${localeToCountryUrlPrefix(locale)}${returnUrl}`
  return `${signinPath}?return_url=${encodeURIComponent(prefixedReturnUrl)}`
}

/**
 * 쿠키 값에 안전하지 않은 문자들을 최소한으로 인코딩합니다
 * @param value 인코딩할 값
 * @returns 쿠키에 안전한 값
 */
const encodeCookieValue = (value: string): string => {
  // 쿠키 파싱을 방해하는 문자들만 인코딩: ; , \ " 및 제어문자
  return (
    value
      .replace(/;/g, '%3B')
      .replace(/,/g, '%2C')
      .replace(/\\/g, '%5C')
      .replace(/"/g, '%22')
      // eslint-disable-next-line no-control-regex
      .replace(/[\x00-\x1F\x7F]/g, (char) => `%${char.charCodeAt(0).toString(16).toUpperCase().padStart(2, '0')}`)
  )
}

/**
 * return_url 쿠키를 안전하게 설정합니다
 * @param returnUrl 저장할 URL (쿠키에 안전하지 않은 문자만 최소 인코딩)
 * @param maxAge 쿠키 만료 시간 (초 단위, 기본값: 24시간)
 * @param path 쿠키 경로 (기본값: '/')
 */
export const setReturnUrlCookie = (returnUrl: string, maxAge = 24 * 60 * 60, path = '/'): void => {
  if (typeof document === 'undefined') {
    return
  }
  document.cookie = `return_url=${encodeCookieValue(returnUrl)}; path=${path}; Max-Age=${maxAge}; SameSite=Lax`
}

/**
 * return_url_after_sign_up 쿠키를 안전하게 설정합니다
 * @param returnUrl 저장할 URL (쿠키에 안전하지 않은 문자만 최소 인코딩)
 * @param maxAge 쿠키 만료 시간 (초 단위, 기본값: 24시간)
 * @param path 쿠키 경로 (기본값: '/')
 */
export const setReturnUrlAfterSignUpCookie = (returnUrl: string, maxAge = 24 * 60 * 60, path = '/'): void => {
  if (typeof document === 'undefined') {
    return
  }
  document.cookie = `return_url_after_sign_up=${encodeCookieValue(returnUrl)}; path=${path}; Max-Age=${maxAge}; SameSite=Lax`
}

/**
 * 서버사이드에서 return_url 쿠키 헤더를 생성합니다
 * @param returnUrl 저장할 URL (쿠키에 안전하지 않은 문자만 최소 인코딩)
 * @param maxAge 쿠키 만료 시간 (초 단위, 기본값: 24시간)
 * @param path 쿠키 경로 (기본값: '/')
 * @param isProduction production 환경 여부 (Secure 플래그 결정)
 * @returns Set-Cookie 헤더 문자열
 */
export const createReturnUrlCookieHeader = (
  returnUrl: string,
  maxAge = 24 * 60 * 60,
  path = '/',
  isProduction = process.env.NODE_ENV === 'production',
): string => {
  const secureFlag = isProduction ? 'Secure;' : ''
  return `return_url=${encodeCookieValue(returnUrl)}; path=${path}; Max-Age=${maxAge}; ${secureFlag} SameSite=Lax`
}

/**
 * URL이 유효한지 간단히 검증합니다
 * @param url 검증할 URL
 * @returns 유효한 URL 여부
 */
export const isValidUrl = (url: string): boolean => {
  if (!url || typeof url !== 'string') {
    return false
  }

  // 상대 경로는 허용
  if (url.startsWith('/')) {
    return true
  }

  // 절대 URL 검증
  try {
    new URL(url)
    return true
  } catch {
    return false
  }
}

/**
 * return_url을 안전하게 정리합니다 (XSS 방지)
 * @param returnUrl 정리할 URL
 * @param fallbackUrl 유효하지 않을 때 사용할 기본 URL
 * @returns 정리된 안전한 URL
 */
export const sanitizeReturnUrl = (returnUrl: string | null | undefined, fallbackUrl = '/'): string => {
  if (!returnUrl || !isValidUrl(returnUrl)) {
    return fallbackUrl
  }

  // 자바스크립트 프로토콜 차단
  if (returnUrl.toLowerCase().startsWith('javascript:')) {
    return fallbackUrl
  }

  return returnUrl
}

/**
 * 쿠키 값 가져오기 (서버/클라이언트 환경 자동 감지)
 * @param name 쿠키 이름
 * @param context 서버사이드 컨텍스트 (선택사항, SSR/SSG에서만 필요)
 * @returns 쿠키 값 또는 null
 */
export const getCookie = (name: string, context?: { req?: any }): string | null => {
  try {
    // 서버사이드 환경
    if (typeof window === 'undefined') {
      if (!context?.req?.headers?.cookie) {
        return null
      }

      // 서버사이드에서 쿠키 파싱
      const cookies = context.req.headers.cookie.split(';').reduce((acc: Record<string, string>, cookie: string) => {
        const [key, ...valueParts] = cookie.trim().split('=')
        if (key && valueParts.length > 0) {
          acc[key] = decodeURIComponent(valueParts.join('='))
        }
        return acc
      }, {})

      return cookies[name] || null
    }

    // 클라이언트사이드 환경
    if (typeof document === 'undefined') {
      return null
    }

    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift() || null
    }
    return null
  } catch (error) {
    console.warn(`Failed to read cookie '${name}':`, error)
    return null
  }
}
